import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { ArrowLeft, Copy, Check, Trash2, Sparkles, Pencil, Save, X, RotateCcw, Wand2 } from "lucide-react";

export default function PromptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/prompts/${id}`);
        setDoc(data);
        setDraft(data.prompt || "");
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
  }, [id]);

  const copy = async () => {
    const text = editing ? draft : doc.prompt;
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      setError("Tidak dapat menyalin. Sila pilih teks dan salin secara manual.");
    }
  };

  const startEdit = () => {
    setDraft(doc.prompt || "");
    setError("");
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(doc.prompt || "");
    setError("");
    setEditing(false);
  };

  const resetToOriginal = () => {
    setDraft(doc.prompt || "");
  };

  const save = async () => {
    const text = draft.trim();
    if (!text) {
      setError("Prompt tidak boleh kosong.");
      return;
    }
    if (text === (doc.prompt || "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/prompts/${id}`, { prompt: text });
      setDoc(data);
      setDraft(data.prompt || "");
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/prompts/${id}`);
      navigate("/history");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setShowConfirm(false);
    }
  };

  if (error && !doc) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-red-400">{error}</div>
    );
  }

  if (!doc) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-neutral-500">Memuatkan…</div>;
  }

  const isDirty = editing && draft.trim() !== (doc.prompt || "").trim();
  const charCount = (editing ? draft : doc.prompt || "").length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="prompt-detail">
      <Link to="/history" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-amber-400 mb-6">
        <ArrowLeft size={16} /> Kembali ke Sejarah
      </Link>

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2 font-mono">
          {doc.model} · {new Date(doc.created_at).toLocaleString("ms-MY")}
          {doc.edited_at && (
            <span className="text-neutral-500 ml-2" data-testid="edited-marker">
              · disunting {new Date(doc.edited_at).toLocaleString("ms-MY")}
            </span>
          )}
        </div>
        <h1 className="font-display text-4xl font-bold">{doc.poster_type}</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {Object.entries(doc.selectors || {}).map(([k, v]) => (
          <div key={k} className="bg-[#101010] border border-white/10 rounded-md p-3" data-testid={`meta-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">{k}</div>
            <div className="text-sm text-white mt-1">{v}</div>
          </div>
        ))}
        <div className="bg-[#101010] border border-white/10 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Saiz</div>
          <div className="text-sm text-white mt-1">
            {doc.size?.width}×{doc.size?.height} {doc.size?.unit}
          </div>
        </div>
        <div className="bg-[#101010] border border-white/10 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Orientasi</div>
          <div className="text-sm text-white mt-1 capitalize">{doc.orientation}</div>
        </div>
        {doc.custom_color && (
          <div className="bg-[#101010] border border-white/10 rounded-md p-3 flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-full border border-white/20"
              style={{ background: doc.custom_color }}
            />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Warna</div>
              <div className="text-sm text-white font-mono">{doc.custom_color}</div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#0F0F0F] border border-amber-500/20 rounded-xl overflow-hidden" data-testid="prompt-output-card">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-[#141414] gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-xs font-mono uppercase tracking-wider text-amber-500/80">
              {editing ? "Sunting Prompt" : "Prompt Dijana"}
            </div>
            {isDirty && (
              <span className="text-[10px] font-mono uppercase text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-0.5" data-testid="dirty-marker">
                belum disimpan
              </span>
            )}
            {justSaved && (
              <span className="text-[10px] font-mono uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5" data-testid="saved-marker">
                <Check size={10} className="inline -mt-0.5" /> disimpan
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {error && editing && (
              <span className="text-xs text-red-400 mr-2" data-testid="edit-error">{error}</span>
            )}
            {!editing && error && (
              <span className="text-xs text-red-400 mr-2" data-testid="copy-error">{error}</span>
            )}
            {!editing ? (
              <>
                <button
                  onClick={() => navigate(`/generator?from=${id}`)}
                  className="flex items-center gap-2 text-xs font-semibold bg-amber-500 text-black hover:bg-amber-400 transition-colors px-3 py-1.5 rounded-md"
                  data-testid="edit-in-generator-btn"
                  title="Buka semula di Penjana dengan semua tetapan asal"
                >
                  <Wand2 size={14} /> Sunting Semula
                </button>
                <button
                  onClick={startEdit}
                  className="flex items-center gap-2 text-xs text-neutral-300 hover:text-amber-400 transition-colors px-3 py-1.5 rounded-md hover:bg-white/5"
                  data-testid="edit-prompt-btn"
                  title="Edit teks prompt secara terus di sini"
                >
                  <Pencil size={14} /> Sunting Teks
                </button>
                <button
                  onClick={copy}
                  className="flex items-center gap-2 text-xs text-neutral-300 hover:text-amber-400 transition-colors px-3 py-1.5 rounded-md hover:bg-white/5"
                  data-testid="copy-prompt-btn"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Disalin!" : "Salin"}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={resetToOriginal}
                  className="flex items-center gap-2 text-xs text-neutral-400 hover:text-amber-400 transition-colors px-3 py-1.5 rounded-md hover:bg-white/5"
                  title="Reset kepada teks asal"
                  data-testid="reset-prompt-btn"
                >
                  <RotateCcw size={14} /> Reset
                </button>
                <button
                  onClick={cancelEdit}
                  className="flex items-center gap-2 text-xs text-neutral-400 hover:text-red-400 transition-colors px-3 py-1.5 rounded-md hover:bg-white/5"
                  data-testid="cancel-edit-btn"
                >
                  <X size={14} /> Batal
                </button>
                <button
                  onClick={save}
                  disabled={saving || !draft.trim()}
                  className="flex items-center gap-2 text-xs bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-prompt-btn"
                >
                  <Save size={14} /> {saving ? "Menyimpan…" : "Simpan"}
                </button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div className="p-4">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              rows={16}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-md text-neutral-100 font-mono text-sm leading-relaxed resize-y focus:border-amber-500/40 focus:outline-none"
              data-testid="prompt-edit-textarea"
              placeholder="Sunting prompt anda di sini…"
            />
            <div className="flex items-center justify-between text-[10px] text-neutral-500 font-mono mt-2">
              <div>Anda boleh ubah, tambah atau padam mana-mana bahagian. Klik <span className="text-amber-400">Simpan</span> untuk kekalkan perubahan.</div>
              <div data-testid="char-count">{charCount.toLocaleString()} aksara</div>
            </div>
          </div>
        ) : (
          <pre className="p-6 text-sm text-neutral-100 whitespace-pre-wrap font-mono leading-relaxed">
            {doc.prompt}
          </pre>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => setShowConfirm(true)}
          className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors"
          data-testid="delete-prompt-btn"
        >
          <Trash2 size={14} /> Padam
        </button>
        <Link to="/generator" className="btn-primary inline-flex items-center gap-2">
          <Sparkles size={16} /> Jana Baharu
        </Link>
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowConfirm(false)}
          data-testid="confirm-delete-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-[#101010] border border-red-500/30 rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <Trash2 className="text-red-400" size={18} />
              </div>
              <h3 className="font-display text-xl font-bold">Padam Prompt?</h3>
            </div>
            <p className="text-sm text-neutral-400 mb-5">
              Prompt ini akan dipadam kekal. Tindakan ini tidak boleh dibatalkan.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded-md text-neutral-300 hover:bg-white/5" data-testid="cancel-delete-btn">Batal</button>
              <button onClick={del} className="px-4 py-2 rounded-md bg-red-500 text-white font-semibold hover:bg-red-400 transition-colors" data-testid="confirm-delete-btn">Ya, Padam</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
